import React, { useState, useEffect, useMemo } from 'react';
import Button from '../common/Button';
import { formatPrice } from '../../utils/formatting';
import { checkCancelEligibility } from '../../utils/time';
import { useContract } from '../../hooks/useContract';
import RenewLicenseModal from '../modals/RenewLicenseModal';

const NFTCard = ({
  nft,
  account,
  mode,
  onBuy,
  onListForSale,
  onCancelListing,
  onViewDetails,
  onRenewSuccess,
}) => {
  // Get contract utilities
  const { contract, getExpirationInfo, getRenewEnabled, getRemainingLicenseTime } = useContract(account);

  // State for renewal modal
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewalInfo, setRenewalInfo] = useState({
    enabled: false,
    canRenew: false,
    remainingTime: 0,
  });

  // Constants from contract
  const MAX_TOTAL_LICENSE = 365 * 24 * 60 * 60 + 180 * 24 * 60 * 60; // 18 months in seconds

  // Memoize ownership check to prevent unnecessary recalculations
  const isOwnNFT = useMemo(() =>
    nft.owner?.toLowerCase() === account?.toLowerCase() ||
    nft.seller?.toLowerCase() === account?.toLowerCase(),
    [nft.owner, nft.seller, account]
  );

  // Load renewal information when component mounts or dependencies change
  const loadRenewalInfo = async () => {
    if (!contract || !nft.id || !isOwnNFT) return;

    try {
      const [renewEnabled, remainingTime] = await Promise.all([
        getRenewEnabled(),
        getRemainingLicenseTime(nft.id)
      ]);

      setRenewalInfo({
        enabled: renewEnabled,
        remainingTime,
        canRenew: renewEnabled && remainingTime > 0 && remainingTime < 365 * 24 * 60 * 60 * 1000 // Less than 1 year remaining
      });
    } catch (error) {
      console.error('Error loading renewal info:', error);
      setRenewalInfo({
        enabled: false,
        remainingTime: 0,
        canRenew: false
      });
    }
  };

  useEffect(() => {
    loadRenewalInfo();
  }, [contract, nft.id, isOwnNFT, getRenewEnabled, getRemainingLicenseTime]);

  // Calculate expiration info
  const expirationInfo = useMemo(() =>
    getExpirationInfo(nft.expires_at),
    [nft.expires_at, getExpirationInfo]
  );

  // State for fresh contract data
  const [freshExpirationInfo, setFreshExpirationInfo] = useState(null);

  // Fetch fresh expiration data from contract if API shows lifetime but NFT might have expiration
  useEffect(() => {
    const fetchFreshExpiration = async () => {
      // Only fetch if API shows lifetime and we have a contract
      if (expirationInfo.isLifetime && contract && nft.id) {
        try {
          const nftInfo = await contract.methods.getNFTInfo(nft.id).call();
          const freshExpiresAt = nftInfo[6]; // expiresAt is at index 6

          if (freshExpiresAt && freshExpiresAt !== '0' && freshExpiresAt !== 0) {
            const freshInfo = getExpirationInfo(freshExpiresAt.toString());
            setFreshExpirationInfo(freshInfo);
          }
        } catch (error) {
          console.error(`Error fetching fresh expiration data for token ${nft.id}:`, error);
        }
      }
    };

    fetchFreshExpiration();
  }, [expirationInfo.isLifetime, contract, nft.id, getExpirationInfo]);

  // Use fresh data if available, otherwise use API data
  const finalExpirationInfo = freshExpirationInfo || expirationInfo;

  const imageUrl = nft.image || null;
  const [cancelEligibility, setCancelEligibility] = useState({ 
    canCancel: true, 
    timeRemaining: 0, 
    timeRemainingText: '' 
  });

  // Update cancel eligibility when listedAt changes
  useEffect(() => {
    if (nft.listedAt && nft.listedAt !== '0') {
      const eligibility = checkCancelEligibility(nft.listedAt);
      setCancelEligibility(eligibility);
      
      // Update the countdown every second if not yet eligible
      if (!eligibility.canCancel) {
        const timer = setInterval(() => {
          const updatedEligibility = checkCancelEligibility(nft.listedAt);
          setCancelEligibility(updatedEligibility);
          
          if (updatedEligibility.canCancel) {
            clearInterval(timer);
          }
        }, 1000);
        
        return () => clearInterval(timer);
      }
    } else {
      setCancelEligibility({ canCancel: true, timeRemaining: 0, timeRemainingText: '' });
    }
  }, [nft.listedAt]);

  // Memoize license type extraction
  const { licenseType, isPro } = useMemo(() => {
    let type = 'Normal';
    if (nft.attributes && Array.isArray(nft.attributes)) {
      const tierAttribute = nft.attributes.find(attr => 
        attr.trait_type === 'Tier' || attr.trait_type === 'tier'
      );
      type = tierAttribute ? tierAttribute.value : 'Normal';
    } else {
      type = nft.tier === 2 ? 'Pro' : 'Normal';
    }
    return { licenseType: type, isPro: type === 'Pro' };
  }, [nft.attributes, nft.tier]);

  return (
    <div 
      className="group relative bg-dark-card backdrop-blur-sm rounded-2xl overflow-hidden border border-dark-border transition-colors duration-200 hover:bg-dark-surface hover:shadow-xl hover:shadow-etc-primary/20 hover:border-etc-primary/30" 
      style={{ 
        contain: 'layout style paint',
        minHeight: '500px',
        height: 'auto'
      }}
    >
      {/* Glow effect on hover */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-etc-primary/10 via-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
      
      {/* Image Section */}
      <div className="h-56 bg-gradient-to-br from-dark-surface to-dark-bg flex items-center justify-center relative overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={nft.name}
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="bg-dark-surface backdrop-blur-sm border border-dark-border rounded-xl w-32 h-32 flex items-center justify-center">
            <span className="text-text-muted text-sm">NFT Image</span>
          </div>
        )}
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        {/* Hover overlay content */}
        <div 
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 cursor-pointer"
          onClick={() => onViewDetails && onViewDetails(nft)}
        >
          <div className="bg-dark-card/90 backdrop-blur-sm border border-etc-primary/30 rounded-lg px-4 py-2 shadow-lg">
            <span className="text-etc-primary text-sm font-medium">Click to view details</span>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5 relative flex flex-col" style={{ minHeight: '244px' }}>
        {/* NFT Name and ID */}
        <div className="mb-3">
          <h3 className="font-bold text-lg text-text-primary mb-1.5 transition-colors duration-300 group-hover:text-etc-primary">
            {nft.name}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-muted text-xs">ID: #{nft.tokenId}</span>
            {isOwnNFT && (
              <span className="bg-etc-primary/20 text-etc-primary text-xs px-2 py-0.5 rounded-full border border-etc-primary/30">
                Owned
              </span>
            )}
          </div>
        </div>

        {/* Tier Badge */}
        <div className="mb-3">
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full shadow-lg transition-all duration-300 ${
            isPro
              ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border border-yellow-500/30 shadow-yellow-500/10'
              : 'bg-gradient-to-r from-blue-600/20 to-blue-700/20 text-blue-300 border border-blue-500/30 shadow-blue-500/10'
          }`}>
            <span className="mr-1 text-xs">{isPro ? '⭐' : '🛡️'}</span>
            {licenseType} License
          </span>
        </div>

        {/* Expiration Info */}
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="text-text-muted text-xs uppercase tracking-wide">Valid</span>
            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
              finalExpirationInfo.isExpired
                ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                : expirationInfo.isLifetime
                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
            }`}>
              {expirationInfo.isExpired ? '⏰' : expirationInfo.isLifetime ? '♾️' : '⏳'}
              {expirationInfo.remainingTime}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-text-secondary text-sm mb-4 line-clamp-3 transition-colors duration-300 group-hover:text-text-primary leading-relaxed">
          {nft.description || 'No description available.'}
        </p>

        {/* Price and Action Section */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex flex-col gap-3">
            {/* Price Display */}
            <div>
              <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Price</p>
              {nft.price === '0' ? (
                <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-dark-surface/50 border border-white/10">
                  <span className="text-text-muted text-sm font-medium">Not for sale</span>
                </div>
              ) : (
                <p className="font-semibold text-xl text-etc-primary transition-colors duration-300 group-hover:text-etc-secondary">
                  {formatPrice(nft.price)}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="w-full">
              {/* Show expired overlay if license is expired */}
              {expirationInfo.isExpired && (
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
                  <div className="bg-red-500/90 text-white px-3 py-1 rounded-full text-sm font-medium">
                    License Expired
                  </div>
                </div>
              )}

              {mode === 'All' ? (
                // In All mode, show Buy button or Cancel if user is the seller
                nft.seller?.toLowerCase() === account?.toLowerCase() && nft.price !== '0' ? (
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="danger"
                      onClick={() => onCancelListing(nft.tokenId)}
                      disabled={!cancelEligibility.canCancel || expirationInfo.isExpired}
                      size="sm"
                      className="w-full"
                      title={
                        finalExpirationInfo.isExpired
                          ? 'Cannot cancel expired license'
                          : !cancelEligibility.canCancel
                          ? `Can cancel in ${cancelEligibility.timeRemainingText}`
                          : 'Cancel listing'
                      }
                    >
                      <span className="mr-1">🚫</span> Cancel
                    </Button>
                    {!cancelEligibility.canCancel && !expirationInfo.isExpired && (
                      <span className="text-xs text-text-muted text-center">
                        Cancel in {cancelEligibility.timeRemainingText}
                      </span>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="success"
                    onClick={() => onBuy(nft.tokenId)}
                    disabled={nft.price === '0' || expirationInfo.isExpired}
                    size="sm"
                    className="w-full"
                    title={expirationInfo.isExpired ? 'Cannot buy expired license' : 'Buy license'}
                  >
                    <span className="mr-1">🛒</span> Buy
                  </Button>
                )
              ) : (
                // In My mode, show List/Cancel based on listing status
                <div className="flex flex-col gap-2">
                  {nft.price === '0' ? (
                    <Button
                      variant="secondary"
                      onClick={() => onListForSale(nft.tokenId)}
                      disabled={!isOwnNFT || expirationInfo.isExpired || !expirationInfo.canList}
                      size="sm"
                      className="w-full"
                      title={
                        finalExpirationInfo.isExpired
                          ? 'Cannot list expired license'
                          : !expirationInfo.canList
                          ? 'License expires too soon to list'
                          : 'List for sale'
                      }
                    >
                      <span className="mr-1">📋</span> List
                    </Button>
                  ) : (
                    <Button
                      variant="danger"
                      onClick={() => onCancelListing(nft.tokenId)}
                      disabled={!isOwnNFT || !cancelEligibility.canCancel || expirationInfo.isExpired}
                      size="sm"
                      className="w-full"
                      title={
                        finalExpirationInfo.isExpired
                          ? 'Cannot cancel expired license'
                          : !cancelEligibility.canCancel
                          ? `Can cancel in ${cancelEligibility.timeRemainingText}`
                          : 'Cancel listing'
                      }
                    >
                      <span className="mr-1">🚫</span> Cancel
                    </Button>
                  )}
                  
                  {/* Renew Button - only show for owned NFTs in My mode */}
                  {isOwnNFT && renewalInfo.canRenew && (
                    <Button
                      variant="primary"
                      onClick={() => setShowRenewModal(true)}
                      disabled={!renewalInfo.enabled || expirationInfo.isExpired}
                      size="sm"
                      className="w-full"
                      title={
                        !renewalInfo.enabled
                          ? 'Renewals are disabled'
                          : expirationInfo.isExpired
                          ? 'Cannot renew expired license'
                          : 'Renew license'
                      }
                    >
                      <span className="mr-1">🔄</span> Renew
                    </Button>
                  )}
                  
                  {!cancelEligibility.canCancel && !expirationInfo.isExpired && nft.price !== '0' && (
                    <span className="text-xs text-text-muted text-center">
                      Cancel in {cancelEligibility.timeRemainingText}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Renew License Modal */}
      <RenewLicenseModal
        isOpen={showRenewModal}
        onClose={() => setShowRenewModal(false)}
        nft={nft}
        account={account}
        onSuccess={(txHash, renewedNft) => {
          setShowRenewModal(false);
          if (onRenewSuccess) {
            onRenewSuccess(txHash, renewedNft);
          }
        }}
        onError={(error) => {
          console.error('Renewal error:', error);
        }}
      />
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(NFTCard, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.nft.tokenId === nextProps.nft.tokenId &&
    prevProps.nft.price === nextProps.nft.price &&
    prevProps.nft.owner === nextProps.nft.owner &&
    prevProps.nft.seller === nextProps.nft.seller &&
    prevProps.nft.listedAt === nextProps.nft.listedAt &&
    prevProps.nft.expires_at === nextProps.nft.expires_at &&
    prevProps.nft.valid === nextProps.nft.valid &&
    prevProps.account === nextProps.account &&
    prevProps.mode === nextProps.mode
  );
});
